/**
 * StaffAvailabilityTab — one person's RECURRING weekly availability, and the
 * three surfaces beside it that are declared coming soon.
 *
 * Every window here is real: it reads and writes
 * `/v1/admin/staff/{id}/availability` (contract 2026-09-11) and, in the design
 * environment, the same record the slot resolver reads — so removing a Tuesday
 * window removes Tuesday slots from the booking journey. Nothing on this tab
 * is a local-only edit.
 *
 * BUILT FROM THE DESIGN SYSTEM (2026-09-11 review): Section owns every card
 * shell and its header rhythm, Banner the notices, Field + Dropdown +
 * TimeSelect the editor controls, Table the list, RowActions the per-row
 * cluster, StatusPill the method tags, InlineEmptyState the empty states. The
 * first pass hand-rolled all of those; nothing here re-forks a DS part.
 *
 * NOT `WeeklyHours`, and that is the one deliberate refusal: it is the DS's
 * weekly-pattern editor but it carries `{start,end}` only, and a window here
 * also carries HOW the person can be reached (in person / video / phone) and
 * which services it narrows to. Adopting it would silently drop both on the
 * first save — the seeded video-only Tuesdays would come back as plain hours.
 *
 * What this tab deliberately does NOT offer: a date. A one-off closure or a
 * holiday is an ABSENCE, a different record with a date range, and the
 * Absences tab says so rather than this one growing a field nothing stores.
 */
import { useMemo, useState } from "react";
import {
  Banner, Btn, Checkbox, Dropdown, Field, Icon, IconTile, InlineEmptyState,
  RowActions, Section, Skeleton, StatusPill, Table, TimeSelect, T, formatTimeSelectLabel,
} from "@v0y4ge/jrni-ui";
import { LoadState } from "../../shell/composites";
import { toast } from "../../shell/toast";
import { useT } from "../../lib/i18n";
import { SR_ONLY } from "../../lib/loading-skeletons";
import {
  useStaffAvailability, displayZoneName, zoneSource, WEEKDAYS, WEEKDAY_LABEL, type AvailabilityRule, type ZoneSource,
} from "./useStaffAvailability";

/** Every panel on the person page shares this measure so the tabs line up. */
const PANEL: React.CSSProperties = { width: "100%", maxWidth: 880, display: "flex", flexDirection: "column", gap: 14 };

interface DayGroup { day: AvailabilityRule["day"]; rules: AvailabilityRule[] }

/**
 * The zone line while the availability read is in flight: the same compact
 * info Banner the settled line uses, so nothing jumps when it lands, with a
 * Skeleton bar where the sentence goes. Banner is `role="status"`, so the
 * visually hidden sentence is what a screen reader hears. Never "no time zone
 * is recorded" — that is a statement about a read that has not answered yet.
 */
export function ZoneLoadingBanner() {
  const t = useT();
  return (
    <div aria-busy="true" data-testid="staff-zone-loading">
      <Banner tone="info" size="compact"
        title={<>
          <span style={SR_ONLY}>{t("staffAvailability.tzLoading", "Loading this person's time zone…")}</span>
          <Skeleton variant="text" width="72%" height={13} />
        </>} />
    </div>
  );
}

/**
 * The ONE time-zone notice for the person page. Availability and Absences share it,
 * so the same fact always reads the same way: plain words, the zone named once, and
 * when something needs fixing, what it means on this tab and where to fix it.
 */
export function StaffZoneBanner({ zone, source, valid, consequence, onFix }: {
  /** The effective zone the server answered, or null when none is recorded. */
  zone: string | null;
  source: ZoneSource;
  /** False when the recorded value is not a real time zone. */
  valid: boolean;
  /** One sentence for the problem case, specific to the tab. */
  consequence: string;
  onFix?: () => void;
}) {
  const t = useT();
  const name = zone ? displayZoneName(zone) : "";
  if (!zone || !valid) {
    return (
      <Banner tone="warning" size="compact" testid="staff-zone-missing"
        title={zone
          ? t("staffZone.invalidTitle", "This person's time zone isn't valid")
          : t("staffZone.missingTitle", "No time zone set for this person")}
        action={onFix ? <Btn size="sm" variant="secondary" onClick={onFix}>{t("staffZone.setZone", "Set time zone")}</Btn> : undefined}>
        <ZoneNote>{consequence} {(onFix ? t("staffZone.fixHint", "Set it on this person's staff record.") : t("staffZone.askAdmin", "Ask someone who manages staff to set it."))}</ZoneNote>
      </Banner>
    );
  }
  const line = source === "own"
    ? t("staffZone.own", "Shown in this person's time zone: {zone}.", { zone: name })
    : source === "venue"
      ? t("staffZone.venue", "Shown in their venue's time zone: {zone}. This person has no time zone of their own.", { zone: name })
      : source === "authored"
        ? t("staffZone.authored", "Shown in {zone}, the time zone these hours were set in.", { zone: name })
        : t("staffZone.neutral", "Shown in {zone}.", { zone: name });
  return <Banner tone="info" size="compact" testid="staff-zone-info" title={<ZoneNote>{line}</ZoneNote>} />;
}

/** A zone sentence as a Banner title. The zone is one unbroken token, so it
 *  wraps whole onto the next line and only breaks if it is wider than the line. */
export function ZoneNote({ children }: { children: React.ReactNode }) {
  return <span data-testid="staff-zone-note" style={{ overflowWrap: "break-word" }}>{children}</span>;
}

/* Meeting methods (CC 0177). A window with no methods recorded, or with all three,
   is unrestricted and reads "Any method". The default for a new window is every
   method: the person's own Meeting type tab is not built yet, so there is nothing
   narrower to read, and in person only would silently hide them from video and
   phone bookings. */
/* CC 0180: methods is optional on the rule. Missing/null = unrestricted. All three
   recorded false admits NO bookings in the slot finder, so it reads "No bookings"
   and edits with none ticked (Save stays off until one is chosen). */
type Methods = NonNullable<AvailabilityRule["methods"]>;
const ALL_METHODS: Methods = { inPerson: true, video: true, phone: true };
const isUnrestricted = (m?: Partial<Methods> | null) => !m || (!!m.inPerson && !!m.video && !!m.phone);
const isNoMethod = (m?: Partial<Methods> | null) => !!m && !m.inPerson && !m.video && !m.phone;
const methodsForEdit = (m?: Partial<Methods> | null): Methods => (!m ? { ...ALL_METHODS } : { inPerson: !!m.inPerson, video: !!m.video, phone: !!m.phone });

function MethodTags({ rule }: { rule: AvailabilityRule }) {
  const t = useT();
  const tags: { key: string; label: string }[] = [];
  if (isUnrestricted(rule.methods)) tags.push({ key: "any", label: "Any method" });
  else if (isNoMethod(rule.methods)) tags.push({ key: "none", label: "No bookings (no method)" });
  else {
    if (rule.methods?.inPerson) tags.push({ key: "inPerson", label: t("staffAvailability.inPerson", "In person") });
    if (rule.methods?.video) tags.push({ key: "video", label: t("staffAvailability.video", "Video") });
    if (rule.methods?.phone) tags.push({ key: "phone", label: t("staffAvailability.phone", "Phone") });
  }
  return (
    <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
      {tags.map((x) => <StatusPill key={x.key} tone="neutral" size="sm">{x.label}</StatusPill>)}
      {rule.services.length ? (
        <StatusPill tone="info" size="sm">
          {rule.services.length + " " + t("staffAvailability.servicesOnly", "services only")}
        </StatusPill>
      ) : null}
    </div>
  );
}

/** The add/edit form for ONE window. Draft state only — the caller commits. */
function RuleEditor({ rule, onCancel, onCommit, busy }: {
  rule: AvailabilityRule;
  onCancel: () => void;
  onCommit: (r: AvailabilityRule) => void;
  busy: boolean;
}) {
  const t = useT();
  const [day, setDay] = useState<AvailabilityRule["day"]>(rule.day);
  const [start, setStart] = useState(rule.start);
  const [end, setEnd] = useState(rule.end);
  /* Meeting methods are back on the window (CC 0177, 5 Oct): without them every
     window saved here was in person only, so a person edited on this screen could
     never be booked by video or phone. Existing windows show what they saved; an
     unrestricted window shows all three ticked. */
  const [methods, setMethods] = useState<Methods>(() => methodsForEdit(rule.methods));
  const noMethod = !methods.inPerson && !methods.video && !methods.phone;
  const badTimes = !(end > start);

  return (
    <Section
      density="content"
      title={rule.id ? t("staffAvailability.editWindow", "Edit window") : t("staffAvailability.addWindow", "Add a window")}
      description={t("staffAvailability.appliesEveryWeek", "Applies every week at the same time.")}
    >
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 14, marginTop: 14 }}>
        <Field label={t("staffAvailability.day", "Day")}>
          <Dropdown
            value={day}
            onChange={(v) => setDay(v as AvailabilityRule["day"])}
            options={WEEKDAYS.map((d) => ({ value: d, label: WEEKDAY_LABEL[d] }))}
          />
        </Field>
        <TimeSelect label={t("staffAvailability.from", "From")} value={start} onChange={setStart} allowEmpty={false} />
        <TimeSelect label={t("staffAvailability.to", "To")} value={end} onChange={setEnd} allowEmpty={false}
          {...(badTimes ? { wrapperStyle: { outline: "none" } } : {})} />
      </div>

      <Field label="Meeting methods" style={{ marginTop: 14 }}>
        <div style={{ display: "flex", gap: 20, flexWrap: "wrap", paddingTop: 4 }} data-testid="availability-methods">
          <Checkbox label="In person" checked={methods.inPerson} onChange={(v) => setMethods((m) => ({ ...m, inPerson: v }))} />
          <Checkbox label="Video" checked={methods.video} onChange={(v) => setMethods((m) => ({ ...m, video: v }))} />
          <Checkbox label="Phone" checked={methods.phone} onChange={(v) => setMethods((m) => ({ ...m, phone: v }))} />
        </div>
      </Field>
      {noMethod ? (
        <Banner tone="danger" size="compact" style={{ marginTop: 12 }} title="Choose at least one way customers can meet this person in this window." />
      ) : null}

      {badTimes ? (
        <Banner tone="danger" size="compact" style={{ marginTop: 12 }}
          title={t("staffAvailability.endAfterStart", "A window has to end after it starts.")} />
      ) : null}

      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap", marginTop: 14 }}>
        <Btn size="sm" variant="secondary" onClick={onCancel}>{t("common.actions.cancel", "Cancel")}</Btn>
        <Btn size="sm" disabled={busy || badTimes || noMethod}
          onClick={() => onCommit({ ...rule, day, start, end, methods })}>
          {busy ? t("staffAvailability.saving", "Saving…") : t("common.actions.save", "Save")}
        </Btn>
      </div>
    </Section>
  );
}

export function StaffAvailabilityTab({ staffId, personName, ownTimezone, onOpenDetails }: {
  /** Opens the tab where the person's time zone is set (the zone banner's fix). */
  onOpenDetails?: () => void;
  staffId: string | null;
  personName: string;
  /** The person's OWN zone from the roster record (null = none of their own).
   *  Omitted when the caller cannot tell; the zone line then names the zone
   *  without saying where it came from. */
  ownTimezone?: string | null;
}) {
  const t = useT();
  const av = useStaffAvailability(staffId);
  const [editing, setEditing] = useState<AvailabilityRule | null>(null);

  const groups: DayGroup[] = useMemo(() => WEEKDAYS
    .map((day) => ({ day, rules: av.rules.filter((r) => r.day === day).sort((a, b) => a.start.localeCompare(b.start)) }))
    .filter((g) => g.rules.length), [av.rules]);

  const commit = async (next: AvailabilityRule[], ok: string) => {
    const saved = await av.save(next);
    if (saved) { setEditing(null); toast({ tone: "success", title: ok, subtitle: personName }); }
    else toast({ tone: "danger", title: t("staffAvailability.couldnTSave", "Couldn't save this availability"), subtitle: av.saveError ?? undefined });
  };

  const upsert = (r: AvailabilityRule) => {
    const exists = av.rules.some((x) => x.id === r.id);
    const next = exists ? av.rules.map((x) => (x.id === r.id ? r : x)) : [...av.rules, r];
    void commit(next, exists ? t("staffAvailability.windowUpdated", "Availability updated") : t("staffAvailability.windowAdded", "Availability added"));
  };

  const remove = (r: AvailabilityRule) => {
    void commit(av.rules.filter((x) => x.id !== r.id), t("staffAvailability.windowRemoved", "Availability removed"));
  };

  /* A person with no roster record has no id to hang a pattern on — their
     locations live on the same record, and the Roles tab says the same thing. */
  if (!staffId) {
    return (
      <div style={PANEL}>
        <Section title={t("staffRoles.tabAvailability", "Availability")}
          description={t("staffAvailability.subtitle", "Recurring windows, applied weekly at the same times.")}>
          <InlineEmptyState
            style={{ marginTop: 8 }}
            icon={<IconTile icon={<Icon.calendar size={16} />} />}
            title={t("staffAvailability.noRosterRecord", "No roster record")}
            description={t("staffAvailability.noRosterRecordBody", "Availability is stored on the roster record, and this person doesn't have one — they hold a workspace membership only. Add them to the staff roster to give them a working pattern.")} />
        </Section>
      </div>
    );
  }

  const columns = [
    {
      id: "day",
      header: t("staffAvailability.days", "Days"),
      minWidth: 132,
      cell: (g: DayGroup) => <span style={{ fontWeight: 600, color: T.n1 }}>{WEEKDAY_LABEL[g.day]}</span>,
    },
    {
      id: "time",
      header: t("staffAvailability.time", "Time"),
      minWidth: 220,
      cell: (g: DayGroup) => (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {g.rules.map((r) => (
            <div key={r.id} style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <span style={{ fontVariantNumeric: "tabular-nums", color: T.n1, whiteSpace: "nowrap" }}>
                {formatTimeSelectLabel(r.start)} – {formatTimeSelectLabel(r.end)}
              </span>
              <MethodTags rule={r} />
              <span style={{ flex: 1, minWidth: 0 }} />
              <RowActions
                size={28}
                onEdit={() => setEditing(r)}
                editLabel={t("common.actions.edit", "Edit")}
                moreItems={[{
                  label: t("common.actions.remove", "Remove"),
                  icon: <Icon.x size={13} />,
                  danger: true,
                  onClick: () => remove(r),
                }]}
              />
            </div>
          ))}
        </div>
      ),
    },
  ];

  const zone = displayZoneName(av.timezone);
  const source = zoneSource(av.timezone, ownTimezone, av.rules.length > 0);

  return (
    <div style={PANEL}>
      {/* Loading, failed, none and known are four different answers. Before the
          read lands the line is a skeleton; after a failed read the list below
          carries the error, so this line says nothing it cannot know. */}
      {!av.loaded && !av.error ? <ZoneLoadingBanner /> : av.loaded ? (
        <StaffZoneBanner zone={av.timezone} source={source} valid={!!av.timezone} onFix={onOpenDetails}
          consequence={t("staffZone.availabilityConsequence", "These hours are shown exactly as entered, so bookings may land at the wrong local time.")} />
      ) : null}

      {editing ? (
        <RuleEditor rule={editing} busy={av.saving} onCancel={() => setEditing(null)} onCommit={upsert} />
      ) : null}

      <Section
        density="dense"
        title={t("staffAvailability.recurring", "Recurring availability")}
        description={t("staffAvailability.recurringBlurb", "These windows apply weekly at the same times.")}
        actions={
          /* Not before the read lands: a save replaces the WHOLE set, so a window
             added over an unread pattern would erase every window it never saw. */
          <Btn size="sm" icon={<Icon.plus size={12} />} disabled={av.saving || !!editing || !av.loaded}
            onClick={() => setEditing({ id: "", day: "Mon", start: "09:00", end: "17:00", methods: { ...ALL_METHODS }, services: [] })}>
            {t("staffAvailability.add", "Add")}
          </Btn>
        }
      >
        <div style={{ marginTop: 12 }}>
          <LoadState loading={av.loading && !av.loaded} error={av.error} onRetry={av.reload} skeleton="table"
            loadingLabel={t("staffAvailability.loadingList", "Loading this person's availability…")}>
            {!groups.length ? (
              <InlineEmptyState
                icon={<IconTile icon={<Icon.calendar size={16} />} />}
                title={av.seeded
                  ? t("staffAvailability.noWindows", "No recurring availability")
                  : t("staffAvailability.notRecorded", "No pattern recorded yet")}
                description={av.seeded
                  ? t("staffAvailability.noWindowsBody", "This person works no fixed weekly hours, so nothing can be booked with them until a window is added.")
                  : t("staffAvailability.notRecordedBody", "Nobody has said when this person works. Weekly windows do not add bookable time: customers book within the location's opening hours. Use Absences to block a person's time.")} />
            ) : (
              /* The table keeps its columns on a narrow pane by scrolling
                 sideways rather than crushing the time column. */
              <div style={{ overflowX: "auto" }}>
                <Table columns={columns} rows={groups} getRowId={(g) => g.day} />
              </div>
            )}
          </LoadState>
        </div>
      </Section>
    </div>
  );
}

/**
 * A tab that is DECLARED, not drawn. Absences, Calendar and Meeting type all
 * exist as operator intent with no record, no op and no seed behind them yet —
 * so they say exactly that, inert, instead of rendering controls that would
 * discard whatever was typed into them.
 */
export function ComingSoonTab({ title, blurb, note, icon }: {
  title: string;
  blurb: string;
  note: string;
  icon: React.ReactNode;
}) {
  const t = useT();
  return (
    /* INERT, not merely empty: `pointer-events: none` plus `aria-disabled`, so
       nothing on this panel can be clicked, focused or typed into while the
       record behind it does not exist. */
    <div aria-disabled="true" style={{ ...PANEL, pointerEvents: "none", userSelect: "none", opacity: 0.72 }}>
      <Banner tone="neutral" size="compact" title={note} />
      <Section
        title={
          <span style={{ display: "inline-flex", alignItems: "center", gap: 9, flexWrap: "wrap" }}>
            {title}
            <StatusPill tone="neutral" size="sm">{t("staffRoles.comingSoon", "Coming soon")}</StatusPill>
          </span>
        }
        description={blurb}
        actions={
          <Btn size="sm" variant="secondary" disabled icon={<Icon.plus size={12} />}
            title={t("staffRoles.comingSoonDisabled", "Not built yet")}>
            {t("staffAvailability.add", "Add")}
          </Btn>
        }
      >
        {/* What is missing, named on both sides. A surface that says only
            "coming soon" leaves the reader to guess whether it waits on a
            screen or on a record; these two say it outright. */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", margin: "12px 0 4px" }}>
          <StatusPill tone="warning" size="sm">{t("staffRoles.backendPending", "Backend — pending")}</StatusPill>
          <StatusPill tone="warning" size="sm">{t("staffRoles.frontendPending", "Frontend — pending")}</StatusPill>
        </div>
        <InlineEmptyState
          icon={<IconTile icon={icon} />}
          title={title}
          description={t("staffRoles.comingSoonBody", "Nothing is recorded here yet. No record, no operation and no screen exist behind this tab, so it holds no data and accepts none. It is shown disabled on purpose rather than offering controls that would discard what you typed.")} />
      </Section>
    </div>
  );
}
